import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { showProduct, editProduct, createProduct } from "../../controllers/apiProducts";
import { getTaxCategories } from "../../controllers/apiConfig";
import { getClients, normalizeClientsResponse } from "../../controllers/api_clients";
import { decryptText } from "../../controllers/api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthContext } from "../../context/AuthContext";
import { preventEnterSubmit, FORM_FOOTER_CONTAINER, FORM_BTN_CANCEL, FORM_BTN_SUBMIT } from "../../utils/formHelpers";

function FormProducts({ empresa_id: clienteProp, rol }) {
  // Req 1 v2 (2026-05-13): cliente del JWT — para excluir al propio admin
  // del listado de clientes en el dropdown ("admin no factura a sí mismo").
  const { user } = useContext(AuthContext);
  const ownClienteId = user?.empresa_id;
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const productId = queryParams.get("id");

  const [product, setProduct] = useState(null);
  const [taxCategories, setTaxCategories] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({}); // Estado para errores de validación

  // (2026-05-13) Req 2: para admin, cuando el cliente seleccionado es es_emisor
  // se ofrece un segundo dropdown opcional para asignar el producto a uno de sus
  // terceros. Permite que el admin configure el catalogo del tercero sin
  // necesidad de loguearse con sus credenciales.
  const [selectedEmisorId, setSelectedEmisorId] = useState("");  // cliente padre escogido (emisor)
  const [terceros, setTerceros] = useState([]);
  const [selectedTerceroId, setSelectedTerceroId] = useState(""); // tercero opcional
  // (2026-05-14) Bandera de visibilidad: el checkbox "producto de comision de
  // tercero" solo aparece cuando el cliente seleccionado (admin) o el cliente
  // propio (operador) tiene es_emisor=true. Asi se evita ofrecer la opcion a
  // principales que no facturan a terceros.
  const [selectedClienteEsEmisor, setSelectedClienteEsEmisor] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const categories = await getTaxCategories();
        setTaxCategories(categories);

        let clientsArr = [];
        // (2026-05-14) Detectar si el usuario tiene permitido mover productos
        // entre el propio emisor y sus terceros. Solo aplica para:
        //   - admin (cualquier cliente)
        //   - operador_admin de un emisor (solo entre su emisor y sus terceros)
        // El rol 'operador' edita productos pero NO puede reasignar
        // empresa_id; el rol 'visor' / 'auditor' no edita en absoluto.
        const propioCliente = user?.usuario?.cliente || user?.cliente || null;
        const operadorDeEmisor =
          !!(propioCliente && propioCliente.es_emisor) && rol === "operador_admin";

        if (rol === "admin") {
          // Solo principales + emisores (excluye terceros).
          // El admin debe asignar el producto al emisor o al propio principal;
          // los terceros se ofrecen en un segundo dropdown SOLO si el cliente
          // elegido es es_emisor.
          const clientsData = await getClients({ es_tercero: false });
          const arr = normalizeClientsResponse(clientsData);
          // Excluir el cliente del propio admin (no se factura a sí mismo).
          clientsArr = arr.filter((c) => c.id !== ownClienteId);
          setClients(clientsArr);
        } else if (operadorDeEmisor) {
          // El cliente del operador es el propio emisor; ya sabemos que es
          // es_emisor=true. Cargar terceros de inmediato para que la lista
          // este disponible al renderizar.
          setSelectedClienteEsEmisor(true);
          setSelectedEmisorId(propioCliente.id);
          try {
            const tdata = await getClients({ es_tercero: true, padre_id: propioCliente.id });
            setTerceros(normalizeClientsResponse(tdata));
          } catch (e) {
            setTerceros([]);
          }
        } else if (propioCliente && propioCliente.es_emisor) {
          // (Edge case) rol === admin pero auth devuelve cliente propio emisor.
          // Solo encendemos el flag por compatibilidad; no entra aqui en la
          // practica porque admin cae en el primer branch.
          setSelectedClienteEsEmisor(true);
        }

        if (productId) {
          const data = await showProduct(decryptText(productId));
          setProduct({
            ...data,
            iva_categoria_id: data.iva_categoria_id || "", // asegurar que sea "" si no tiene valor
            // (2026-07-02) SENIAT Fase 1: 'UND' no es un código UNECE válido;
            // el código oficial de "Unidad" es 'C62' (Catálogo 11). Productos
            // viejos con 'UND' se mapean visualmente a C62 y al guardar
            // persisten 'C62'.
            unidad_medida:
              data.unidad_medida === "UND" ? "C62" : (data.unidad_medida || "C62"),
          });
          // (2026-05-14) Al editar, si el empresa_id del producto pertenece a
          // un emisor (o es un tercero hijo de un emisor), habilitar el
          // checkbox de producto-tercero. Tambien pre-seleccionar el emisor
          // y el tercero para que el admin / operador_admin pueda cambiarlos
          // coherentemente.
          if (rol === "admin" && data?.empresa_id) {
            const propietario = clientsArr.find((c) => c.id === data.empresa_id);
            if (propietario) {
              // empresa_id apunta directamente a un principal/emisor.
              setSelectedEmisorId(propietario.id);
              setSelectedClienteEsEmisor(!!propietario.es_emisor);
              if (propietario.es_emisor) {
                try {
                  const tdata = await getClients({ es_tercero: true, padre_id: propietario.id });
                  setTerceros(normalizeClientsResponse(tdata));
                } catch (e) { setTerceros([]); }
              }
            } else {
              // empresa_id no esta en la lista de principales -> es un tercero.
              // Recuperar su emisor (padre) y cargar la lista de terceros.
              try {
                const fullList = await getClients({ es_tercero: true });
                const tercero = normalizeClientsResponse(fullList).find((t) => t.id === data.empresa_id);
                if (tercero && tercero.padre_id) {
                  const emisor = clientsArr.find((c) => c.id === tercero.padre_id);
                  if (emisor) {
                    setSelectedEmisorId(emisor.id);
                    setSelectedClienteEsEmisor(!!emisor.es_emisor);
                    const tdata = await getClients({ es_tercero: true, padre_id: emisor.id });
                    setTerceros(normalizeClientsResponse(tdata));
                    setSelectedTerceroId(tercero.id);
                  }
                }
              } catch (e) { /* silencioso */ }
            }
          } else if (operadorDeEmisor && data?.empresa_id) {
            // (2026-05-14) Operador de emisor editando un producto: si el
            // empresa_id del producto coincide con uno de sus terceros, lo
            // pre-seleccionamos en el dropdown de tercero. Si coincide con
            // su propio emisor, queda sin tercero seleccionado.
            if (data.empresa_id !== propioCliente.id) {
              setSelectedTerceroId(data.empresa_id);
            }
          }
        } else {
          setProduct({
            nombre: "",
            sku: "",
            precio_base: "",
            descripcion: "",
            iva_categoria_id: "", // inicio vacío para que el select no venga seleccionado
            activo: true,
            aplica_iva: true,
            empresa_id: clienteProp || (operadorDeEmisor ? propioCliente.id : ""),
            // SENIAT docElectronico (2026-06-24): se snapshotean en factura_items al emitir.
            // (2026-07-02) SENIAT Fase 1: default 'UND' -> 'C62' (código UNECE
            // oficial de "Unidad", Catálogo 11 GGTIC.GIT.00.02 v1.4).
            tipo: "bien",
            codigo_plu: "",
            unidad_medida: "C62",
            codigo_ciiu: "",
          });
        }
      } catch (err) {
        console.error(err);
        setError("Error al cargar el producto, los impuestos o los clientes");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [productId, rol, clienteProp]);

  if (loading) return <div>Cargando...</div>;
  if (error) return <div>{error}</div>;

  // Validación de campos requeridos
  const validate = () => {
    const newErrors = {};
    var errorToast = [];
    if (!product.sku){
      newErrors.sku = "SKU es obligatorio";
      errorToast.push("- SKU es obligatorio");
    }
    if (!product.nombre){
      newErrors.nombre = "Nombre es obligatorio";
      errorToast.push("- Nombre es obligatorio");
    }
    if (!product.precio_base){
      newErrors.precio_base = "Precio base es obligatorio";
      errorToast.push("- Precio base es obligatorio");
    }
    if (!product.iva_categoria_id){
      newErrors.iva_categoria_id = "Debe seleccionar IVA";
      errorToast.push("- Debe seleccionar IVA");
    }
    if (!product.descripcion){
      newErrors.descripcion = "Descripción es obligatorio";
      errorToast.push("- Descripción es obligatorio");
    }
    if (rol === "admin" && !product.empresa_id){
      newErrors.empresa_id = "Debe seleccionar un cliente";
      errorToast.push("- Debe seleccionar un cliente");
    }
    if (!product.peso_kg){
      newErrors.peso_kg = "Peso (Kg) es obligatorio";
      errorToast.push("- Peso (Kg) es obligatorio");
    }
    if (!product.volumen_m3){
      newErrors.volumen_m3 = "Volumen (M³) es obligatorio";
      errorToast.push("- Volumen (M³) es obligatorio");
    }
    setErrors(newErrors);
    if (errorToast.length > 0){
      toast.error(<div>
        {errorToast.map(item => (
            <span className="text-start">{item}<br/></span>
          ))}
      </div>)
      setButtonDisabled(false);
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setButtonDisabled(true);
    if (!validate()) return;
    try {
      const body = {
        nombre: product.nombre,
        sku: product.sku,
        precio_base: product.precio_base.toString(),
        descripcion: product.descripcion,
        iva_categoria_id: parseInt(product.iva_categoria_id, 10),
        aplica_iva: product.aplica_iva ?? true,
        peso_kg: product.peso_kg,
        volumen_m3: product.volumen_m3,
        // Req 1 (2026-05-13): marca producto de comisión de tercero
        es_tercero: product.es_tercero ?? false,
        // SENIAT docElectronico (2026-06-24)
        // (2026-07-02) SENIAT Fase 1: default y compatibilidad 'UND' -> 'C62'
        // (aunque el estado ya se normaliza al cargar, aquí también por
        // defensa: nunca persistir 'UND').
        tipo: product.tipo || "bien",
        codigo_plu: product.codigo_plu || null,
        unidad_medida:
          (product.unidad_medida === "UND" ? "C62" : product.unidad_medida) || "C62",
        codigo_ciiu: product.codigo_ciiu || null,
      };

      if (product.empresa_id) body.empresa_id = product.empresa_id;

      if (!productId) {
        await createProduct(body);
      } else {
        await editProduct(decryptText(productId), body);
      }

      toast.success("Producto guardado correctamente", {
        onClose: () => navigate("/products"),
      });
    } catch (err) {
      console.error("Error creando/editando producto:", err);
      const msg =
        err.response?.data?.error ||
        err.response?.data ||
        "Error al guardar el producto";
      toast.error(msg);
    } finally {
      setButtonDisabled(false);
    }
  };

  const redirectToList = () => navigate("/products");

  // Función para clases de input según error
  const inputClass = (field) =>
    `border-0 px-3 py-3 rounded text-sm shadow focus:outline-none focus:ring w-full ${
      errors[field] ? "border-red-500 ring-red-200" : "border-gray-200 ring-blue-300"
    }`;

  return (
    <div className="mx-auto w-full">
      <ToastContainer />
      <div className="flex flex-wrap">
        <div className="w-full lg:w-12/12 px-4">
          <div className="relative bg-white flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-blueGray-100 border-0">
            <div className="rounded-t bg-white mb-0 px-6 py-6 flex justify-between">
              <h6 className="text-blueGray-700 text-xl font-bold">
                {productId ? "Actualizar" : "Crear"} Producto/Servicio
              </h6>
            </div>

            <div className="flex-auto px-4 lg:px-10 py-10 pt-0">
              <form onSubmit={handleSubmit} onKeyDown={preventEnterSubmit}>
                {/* FILA 1: SKU | Nombre */}
                <div className="flex flex-wrap -mx-2 mb-4">
                  <div className="w-1/2 px-2">
                    <label className="block text-blueGray-600 text-xs font-bold mb-2">SKU</label>
                    <input
                      type="text"
                      className={inputClass("sku")}
                      value={product.sku}
                      placeholder="Código del producto"
                      onChange={(e) => setProduct({ ...product, sku: e.target.value.toString().toUpperCase() })}
                    />
                    {errors.sku && <p className="text-red-500 text-xs mt-1">{errors.sku}</p>}
                  </div>

                  <div className="w-1/2 px-2">
                    <label className="block text-blueGray-600 text-xs font-bold mb-2">Nombre</label>
                    <input
                      type="text"
                      className={inputClass("nombre")}
                      value={product.nombre}
                      placeholder="Nombre del producto"
                      onChange={(e) => setProduct({ ...product, nombre: e.target.value.toString().toUpperCase() })}
                    />
                    {errors.nombre && <p className="text-red-500 text-xs mt-1">{errors.nombre}</p>}
                  </div>
                </div>

                {/* FILA 2: Precio Base | IVA */}
                <div className="flex flex-wrap -mx-2 mb-4">
                  <div className="w-1/2 px-2">
                    <label className="block text-blueGray-600 text-xs font-bold mb-2">Precio Base</label>
                    <input
                      type="number"
                      step="0.01"
                      className={inputClass("precio_base") + ' text-end'}
                      value={product.precio_base}
                      placeholder="0,00"
                      onChange={(e) => setProduct({ ...product, precio_base: e.target.value })}
                    />
                    {errors.precio_base && <p className="text-red-500 text-xs mt-1">{errors.precio_base}</p>}
                  </div>

                  <div className="w-1/2 px-2">
                    <label className="block text-blueGray-600 text-xs font-bold mb-2">IVA</label>
                    <select
                      className={inputClass("iva_categoria_id")}
                      value={product.iva_categoria_id}
                      onChange={(e) =>
                        setProduct({ ...product, iva_categoria_id: e.target.value })
                      }
                    >
                      <option value="" disabled>
                        -- Seleccione IVA --
                      </option>
                      {taxCategories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.nombre}
                        </option>
                      ))}
                    </select>
                    {errors.iva_categoria_id && <p className="text-red-500 text-xs mt-1">{errors.iva_categoria_id}</p>}
                  </div>
                </div>

                {/* Descripción */}
                <div className="w-full px-0 mb-4">
                  <label className="block text-blueGray-600 text-xs font-bold mb-2">Descripción</label>
                  <textarea
                    className="border-0 px-3 py-3 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full"
                    value={product.descripcion}
                    placeholder="Descripción del producto"
                    onChange={(e) => setProduct({ ...product, descripcion: e.target.value.toString().toUpperCase() })}
                  />
                  {errors.descripcion && <p className="text-red-500 text-xs mt-1">{errors.descripcion}</p>}
                </div>

                <div className="flex flex-wrap -mx-2 mb-4">
                  <div className="w-1/2 px-2">
                    <label className="block text-blueGray-600 text-xs font-bold mb-2">Peso (Kg)</label>
                    <input
                        type="number"
                        step="0.001"
                        className={inputClass("precio_base") + ' text-end'}
                        value={product.peso_kg}
                        placeholder="0,000"
                        onChange={(e) => setProduct({ ...product, peso_kg: e.target.value })}
                      />
                      {errors.peso_kg && <p className="text-red-500 text-xs mt-1">{errors.peso_kg}</p>}
                  </div>

                  <div className="w-1/2 px-2">
                    <label className="block text-blueGray-600 text-xs font-bold mb-2">Volumen (M³)</label>
                    <input
                        type="number"
                        step="0.0001"
                        className={inputClass("precio_base") + ' text-end'}
                        value={product.volumen_m3}
                        placeholder="0,0000"
                        onChange={(e) => setProduct({ ...product, volumen_m3: e.target.value })}
                      />
                      {errors.volumen_m3 && <p className="text-red-500 text-xs mt-1">{errors.volumen_m3}</p>}
                  </div>
                </div>

                {/* --- SENIAT docElectronico (2026-06-24): tipo / unidad de medida / PLU / CIIU --- */}
                <div className="flex flex-wrap -mx-2 mb-4">
                  <div className="w-1/2 px-2">
                    <label className="block text-blueGray-600 text-xs font-bold mb-2">Tipo (SENIAT)</label>
                    <select
                      className={inputClass("tipo")}
                      value={product.tipo || "bien"}
                      onChange={(e) => setProduct({ ...product, tipo: e.target.value })}
                    >
                      <option value="bien">Bien</option>
                      <option value="servicio">Servicio</option>
                    </select>
                  </div>
                  <div className="w-1/2 px-2">
                    <label className="block text-blueGray-600 text-xs font-bold mb-2">Unidad de Medida (UNECE)</label>
                    {/* (2026-07-02) SENIAT Fase 1: 'UND — Unidad' se reemplaza
                        por 'C62 — Unidad' (código UNECE oficial, Catálogo 11).
                        Compatibilidad: productos viejos con 'UND' se normalizan
                        a 'C62' al cargar (ver fetchData) y al armar el payload. */}
                    <select
                      className={inputClass("unidad_medida")}
                      value={(product.unidad_medida === "UND" ? "C62" : product.unidad_medida) || "C62"}
                      onChange={(e) => setProduct({ ...product, unidad_medida: e.target.value })}
                    >
                      <option value="C62">C62 — Unidad</option>
                      <option value="KGM">KGM — Kilogramo</option>
                      <option value="GRM">GRM — Gramo</option>
                      <option value="LTR">LTR — Litro</option>
                      <option value="MTR">MTR — Metro</option>
                      <option value="MTK">MTK — Metro cuadrado</option>
                      <option value="MTQ">MTQ — Metro cúbico</option>
                      <option value="HUR">HUR — Hora</option>
                      <option value="DAY">DAY — Día</option>
                      <option value="MON">MON — Mes</option>
                      <option value="KMT">KMT — Kilómetro</option>
                    </select>
                  </div>
                </div>
                <div className="flex flex-wrap -mx-2 mb-4">
                  <div className="w-1/2 px-2">
                    <label className="block text-blueGray-600 text-xs font-bold mb-2">Código de Barra / PLU</label>
                    <input
                      type="text"
                      className={inputClass("codigo_plu")}
                      value={product.codigo_plu || ""}
                      placeholder="EAN / código de barra (opcional)"
                      onChange={(e) => setProduct({ ...product, codigo_plu: e.target.value.toString().toUpperCase() })}
                    />
                  </div>
                  <div className="w-1/2 px-2">
                    <label className="block text-blueGray-600 text-xs font-bold mb-2">Código CIIU</label>
                    <input
                      type="text"
                      className={inputClass("codigo_ciiu")}
                      value={product.codigo_ciiu || ""}
                      placeholder="Actividad económica (opcional)"
                      onChange={(e) => setProduct({ ...product, codigo_ciiu: e.target.value.toString().toUpperCase() })}
                    />
                  </div>
                </div>
                {/* --- Fin SENIAT --- */}

                {/* --- Req 1 (2026-05-13) / Req (2026-05-14): producto de
                    comision de tercero. Solo visible si el cliente al que se
                    asigna el producto tiene es_emisor=true (admin: el cliente
                    seleccionado en el dropdown; operador / operador_admin: su
                    propio cliente). El CHECKBOX (editable) solo se renderiza
                    para admin y operador_admin. Para operador se muestra como
                    label read-only (SI/NO) para que pueda ver el estado actual
                    del producto sin poder modificarlo. --- */}
                {selectedClienteEsEmisor && (rol === "admin" || rol === "operador_admin") && (
                  <div className="w-full px-2 mb-4">
                    <label className="inline-flex items-center text-sm">
                      <input
                        type="checkbox"
                        className="form-checkbox mr-2"
                        checked={!!product.es_tercero}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          if (checked) {
                            // (2026-05-15) Un "producto de comisión de
                            // terceros" pertenece al EMISOR (sirve para
                            // facturar la comisión), no a un tercero
                            // específico: al activarlo se limpia y
                            // desactiva el campo "Asignar a tercero" y el
                            // empresa_id vuelve al emisor base.
                            const emisorBase = rol === "admin"
                              ? selectedEmisorId
                              : (user?.usuario?.cliente?.id || user?.cliente?.id || clienteProp);
                            setSelectedTerceroId("");
                            setProduct({
                              ...product,
                              es_tercero: true,
                              empresa_id: emisorBase || product.empresa_id,
                            });
                          } else {
                            setProduct({ ...product, es_tercero: false });
                          }
                        }}
                      />
                      <span className="font-bold">Producto de comisión de terceros</span>
                      <span className="ml-2 text-xs text-slate-500">
                        (se factura al tercero al emitir documentos a su nombre)
                      </span>
                    </label>
                  </div>
                )}
                {selectedClienteEsEmisor && rol !== "admin" && rol !== "operador_admin" && (
                  <div className="w-full px-2 mb-4">
                    <p className="text-sm">
                      <span className="font-bold">Producto de comisión de terceros:</span>{" "}
                      <span className={product.es_tercero ? "text-emerald-600 font-bold" : "text-slate-500"}>
                        {product.es_tercero ? "SI" : "NO"}
                      </span>
                      <span className="ml-2 text-xs text-slate-500">
                        (solo admin / operador_admin pueden modificarlo)
                      </span>
                    </p>
                  </div>
                )}
                {/* --- Fin --- */}

                {/* (2026-05-14) Visualizacion read-only del cliente / tercero
                    asignado al producto. Visible solo para roles distintos a
                    admin que esten editando un producto existente. Muestra si
                    el producto pertenece al propio emisor o a un tercero
                    especifico (con nombre y RIF). Sin opcion de modificar. */}
                {productId && rol !== "admin" && rol !== "operador_admin" && product.empresa && (
                  <div className="w-full px-2 mb-4">
                    <label className="block text-blueGray-600 text-xs font-bold mb-2">
                      Asignado a
                    </label>
                    <div className="border-0 px-3 py-3 bg-gray-100 text-blueGray-600 rounded text-sm shadow w-full">
                      {product.empresa.es_tercero ? (
                        <>
                          <span className="font-bold text-twilight-indigo-700">Tercero:</span>{" "}
                          {product.empresa.nombre_empresa} ({product.empresa.rif})
                        </>
                      ) : (
                        <>
                          <span className="font-bold">Empresa (Emisor):</span>{" "}
                          {product.empresa.nombre_empresa} ({product.empresa.rif})
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* (2026-05-14) Bloque "Cliente": SOLO visible para admin.
                    Para cualquier otro rol (incluido operador_admin) el cliente
                    propietario del producto es fijo — es el cliente vinculado al
                    usuario por su JWT — y NO debe ofrecerse como opcion editable.
                    El operador_admin sigue viendo abajo el dropdown de tercero
                    (separado), que es lo unico que puede modificar. */}
                {rol === "admin" && (
                <div className="w-full px-2 mb-4">
                  <label className="block text-blueGray-600 text-xs font-bold mb-2">Empresa</label>
                  {rol === "admin" ? (
                    <select
                      className={inputClass("empresa_id")}
                      value={selectedEmisorId || (product.empresa_id ?? "")}
                      onChange={async (e) => {
                        const val = e.target.value;
                        const cliId = val ? parseInt(val, 10) : "";
                        setSelectedEmisorId(cliId);
                        setSelectedTerceroId("");
                        // (2026-05-14) Al cambiar de cliente reseteamos el
                        // flag es_tercero del producto: si el nuevo cliente
                        // NO es emisor, la opcion deja de estar disponible
                        // (visualmente oculta) y debe quedar en false en BD.
                        const cli = clients.find((c) => c.id === cliId);
                        const esEmisor = !!(cli && cli.es_emisor);
                        setSelectedClienteEsEmisor(esEmisor);
                        setProduct({
                          ...product,
                          empresa_id: cliId || "",
                          // Si el nuevo cliente no es emisor, forzamos es_tercero=false.
                          es_tercero: esEmisor ? (product.es_tercero ?? false) : false,
                        });

                        // Si el cliente es emisor, traer sus terceros para
                        // permitir asignar el producto a uno de ellos.
                        if (esEmisor) {
                          try {
                            const data = await getClients({ es_tercero: true, padre_id: cliId });
                            setTerceros(normalizeClientsResponse(data));
                          } catch (err) {
                            setTerceros([]);
                          }
                        } else {
                          setTerceros([]);
                        }
                      }}
                    >
                      <option value="">Seleccione un cliente</option>
                      {clients.map((cli) => (
                        <option key={cli.id} value={cli.id}>
                          {cli.nombre_empresa} ({cli.rif})
                          {cli.es_emisor ? " — EMISOR" : ""}
                        </option>
                      ))}
                    </select>
                  ) : (
                    // (2026-05-14) Branch defensivo: ya el bloque exterior
                    // restringe rol === "admin", asi que esta rama no se
                    // renderiza en la practica. Se conserva por compatibilidad
                    // si el wrapper externo cambia en el futuro.
                    <input
                      type="text"
                      className="border-0 px-3 py-3 bg-gray-100 text-blueGray-600 rounded text-sm shadow w-full"
                      value={`Empresa #${clienteProp}`}
                      readOnly
                    />
                  )}
                  {errors.empresa_id && <p className="text-red-500 text-xs mt-1">{errors.empresa_id}</p>}
                </div>
                )}

                {/* (2026-05-13) Req 2 / (2026-05-14): asignar el producto a un
                    tercero. Visible en dos casos:
                      - admin: cuando el cliente elegido en el dropdown es emisor
                        y tiene terceros (logica original).
                      - operador / operador_admin de un emisor: siempre que su
                        cliente sea emisor; permite mover el producto entre el
                        propio emisor y cualquier tercero suyo.
                    En ambos casos el empresa_id efectivo del producto cambia al
                    id del tercero (o vuelve al emisor si se deselecciona). */}
                {selectedClienteEsEmisor && terceros.length > 0 && (
                  <div className="w-full px-2 mb-4">
                    <label className="block text-blueGray-600 text-xs font-bold mb-2">
                      Asignar a tercero (opcional)
                    </label>
                    <select
                      className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
                      value={product.es_tercero ? "" : selectedTerceroId}
                      disabled={!!product.es_tercero}
                      onChange={(e) => {
                        const val = e.target.value;
                        const terceroId = val ? parseInt(val, 10) : "";
                        setSelectedTerceroId(terceroId);
                        // El producto se persiste con empresa_id del tercero
                        // si se selecciona; caso contrario queda en el emisor.
                        // Para no-admin el emisor es siempre el propio cliente.
                        const emisorBase = rol === "admin"
                          ? selectedEmisorId
                          : (user?.usuario?.cliente?.id || user?.cliente?.id || clienteProp);
                        setProduct({
                          ...product,
                          empresa_id: terceroId || emisorBase,
                        });
                      }}
                    >
                      <option value="">— Sin tercero (asignar al emisor) —</option>
                      {terceros.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.nombre_empresa} ({t.rif})
                          {t.activo ? "" : " [Inactivo]"}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-blueGray-400 mt-1">
                      {product.es_tercero ? (
                        <span className="text-amber-600">
                          <i className="fas fa-lock mr-1" />
                          Deshabilitado: un producto de comisión de terceros pertenece al emisor, no a un tercero específico.
                        </span>
                      ) : (
                        "Si seleccionas un tercero, el producto quedará vinculado a él (no al emisor)."
                      )}
                    </p>
                  </div>
                )}

                <div className={FORM_FOOTER_CONTAINER}>
                  <button
                    type="button"
                    className={FORM_BTN_CANCEL}
                    disabled={buttonDisabled}
                    onClick={redirectToList}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className={FORM_BTN_SUBMIT}
                    disabled={buttonDisabled}
                  >
                    {buttonDisabled
                      ? (productId ? "Actualizando..." : "Guardando...")
                      : (productId ? "Actualizar" : "Guardar")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FormProducts;
